import { useState } from 'react';
import { updateNodeChartVisibility } from '../../api';
import {
  ITEM_KINDS,
  nodeChartSections,
  nodeHeadingId,
  nodeItemId,
  nodeItemSize
} from '../../config/sectionLayouts';
import NodeChartCard from './NodeChartCard';
import NodeFiltersPanel from './NodeFiltersPanel';
import { groupAllChartsBySection } from './nodeChartLayout';
import { MAX_NODE_FILTERS, buildNodeFilterEntries, selectNodeCatalogFilters } from './nodeFilterUtils';

const EMPTY = { entries: [], hiddenEntries: [], nodeSections: [], hiddenNodeItems: [] };

// Заголовок прижат к своим карточкам, как раньше (`space-y-3`): зазор сетки 24 px, а минус 12 px
// снизу оставляют между заголовком и карточками прежние 12.
const SectionHeading = ({ title, hint }) => (
  <div className="-mb-3">
    <h3 className="text-base font-semibold">{title}</h3>
    <p className="text-xs text-muted-foreground">{hint}</p>
  </div>
);

/**
 * Графики по нодам как элементы сетки вкладки: каждая карточка — отдельный элемент, заголовок
 * секции — тоже, панель фильтров по результатам анализа — тоже.
 *
 * Раньше это была одна секция со своей сеткой (`NodeChartsSection`), и переставить график
 * между статическими панелями вкладки было нельзя. Состав по-прежнему решает каталог пакета:
 * новый пакет появляется на вкладке без правок кода, а его карточки без сохранённого места
 * встают под всеми остальными.
 *
 * Каталог передаётся снаружи, а не загружается здесь: у «Сотрудников» его уже читает шапка
 * раздела для фильтров, и второй экземпляр после скрытия карточки остался бы устаревшим.
 *
 * Кто владеет фильтрами, зависит от вкладки — как и раньше: в «Общей статистике» панель здесь
 * и действует на графики нод, у «Сотрудников» она в шапке раздела (`filtersControlled`).
 */
const useNodeChartItems = ({
  tab,
  nodeCatalog,
  dateRange,
  callType = 'all',
  onOpenCallJournal,
  filtersControlled = false,
  nodeFilter: controlledNodeFilter,
  scope
}) => {
  const { catalog, isLoading, isActive, error, reload } = nodeCatalog;
  const [nodeFilters, setNodeFilters] = useState({});

  // Ошибку каталога показываем явно: иначе недоступный сервер выглядел бы так же, как
  // «пакет не загружен», — то есть никак.
  const notice = error ? (
    <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
      <p className="font-medium text-destructive">{error}</p>
      <button
        type="button"
        data-ev="nodes.catalog.retry"
        onClick={reload}
        className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        Повторить
      </button>
    </div>
  ) : null;

  if (error || isLoading || !isActive) {
    return { ...EMPTY, notice };
  }

  const sectionIds = nodeChartSections(tab);
  const sections = groupAllChartsBySection(catalog.charts ?? [])
    .filter((section) => sectionIds.includes(section.id));

  const setChartVisible = async (chartId, visible) => {
    await updateNodeChartVisibility(chartId, visible);
    reload();
  };

  const activeFilters = selectNodeCatalogFilters(catalog);
  const filterEntries = buildNodeFilterEntries(nodeFilters, activeFilters);
  const ownNodeFilter = filterEntries.length > 0
    ? filterEntries.slice(0, MAX_NODE_FILTERS).join(';')
    : undefined;
  const nodeFilter = filtersControlled ? controlledNodeFilter : ownNodeFilter;

  const entries = [];
  if (!filtersControlled && activeFilters.length > 0) {
    entries.push({
      id: `ui:${tab}.node-filters`,
      kind: ITEM_KINDS.Flow,
      element: (
        <NodeFiltersPanel
          filters={activeFilters}
          values={nodeFilters}
          onChange={(id, value) => setNodeFilters((current) => ({ ...current, [id]: value }))}
          onReset={() => setNodeFilters({})}
          packName={catalog.packName}
          packVersion={catalog.packVersion}
          entryCount={filterEntries.length}
        />
      )
    });
  }

  const nodeSections = [];
  const hiddenEntries = [];
  const hiddenNodeItems = [];

  for (const section of sections) {
    const visible = section.charts.filter(({ spec }) => !spec.hidden);
    if (visible.length > 0) {
      entries.push({
        id: nodeHeadingId(tab, section.id),
        kind: ITEM_KINDS.Heading,
        element: <SectionHeading title={section.title} hint={section.hint} />
      });
    }

    nodeSections.push({
      id: section.id,
      items: visible.map(({ chart, spec }) => ({ id: nodeItemId(chart.id), ...nodeItemSize(spec, section.id) }))
    });

    for (const { chart, spec } of section.charts) {
      const id = nodeItemId(chart.id);
      const { kind } = nodeItemSize(spec, section.id);

      if (spec.hidden) {
        hiddenNodeItems.push({ id, ...nodeItemSize(spec, section.id) });
        hiddenEntries.push({ id, title: chart.title, show: () => setChartVisible(chart.id, true) });
        continue;
      }

      entries.push({
        id,
        kind,
        title: chart.title,
        hide: () => setChartVisible(chart.id, false),
        // packVersion намеренно не передаём: выдача считается по всем версиям пакета, иначе
        // смена набора нод обнуляла бы накопленную статистику.
        element: (
          <NodeChartCard
            chart={chart}
            dateRange={dateRange}
            callType={callType}
            nodeFilter={nodeFilter}
            scope={scope}
            onOpenCallJournal={onOpenCallJournal}
          />
        )
      });
    }
  }

  return { entries, hiddenEntries, nodeSections, hiddenNodeItems, notice };
};

export default useNodeChartItems;
